/**
 * Testimonials section
 * ---------------------------------------------------------------------------
 * Extracted from sections/testimonials.liquid, where it was inline <script>.
 * Inline scripts are parser-blocking, uncacheable, and re-sent on every page
 * view. This asset is deferred and cached, and initialises every instance on
 * the page instead of binding to a single section id.
 *
 * Initialisation is idempotent: an already-wired element is skipped.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-section-type="testimonials"]';

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.testimonialsInit === 'true') return;
    sectionEl.dataset.testimonialsInit = 'true';

    var sectionId = sectionEl.dataset.sectionId;
    const track = sectionEl.querySelector('#TestimonialsTrack-' + sectionId);
    const prevBtn = sectionEl.querySelector('#TestimonialsPrev-' + sectionId);
    const nextBtn = sectionEl.querySelector('#TestimonialsNext-' + sectionId);
    const dots = sectionEl.querySelectorAll('.custom-testimonials__dot');
    const items = sectionEl.querySelectorAll('.custom-testimonials__item');
    const uniqueCount = parseInt(sectionEl.dataset.blockCount, 10) || 1;

    if (!track || items.length === 0) return;

    // Measuring forces a synchronous layout, and this used to run on every
    // scroll event via getSetWidth(). The value only changes when the viewport
    // changes, so it is measured once and invalidated on resize.
    let cachedStep = null;

    function getItemStep() {
      if (cachedStep !== null) return cachedStep;
      const firstItem = items[0];
      if (!firstItem) return 332;
      const itemWidth = firstItem.getBoundingClientRect().width;
      // Do not cache a zero measurement. The element can be unlaid-out on the
      // first read (hidden section, images not yet sized); the old code hid this
      // by re-measuring every time. Caching a 0 here would wedge the carousel.
      if (itemWidth <= 0) return 332;
      const gap = window.innerWidth <= 1024 ? 16 : 22;
      cachedStep = itemWidth + gap;
      return cachedStep;
    }

    function getSetWidth() {
      return uniqueCount * getItemStep();
    }

    // Start in the middle set (Set 2).
    // Deferred to the next frame: doing this during script evaluation forced a
    // layout before first paint, which is charged directly to the critical path.
    requestAnimationFrame(function () {
      track.scrollLeft = getSetWidth();
    });

    let isLoopingReset = false;

    function handleInfiniteScroll() {
      if (isLoopingReset) return;

      const setWidth = getSetWidth();
      const current = track.scrollLeft;

      // Wrap left buffer seamlessly
      if (current < setWidth * 0.4) {
        isLoopingReset = true;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = current + setWidth;
        track.style.scrollBehavior = 'smooth';
        setTimeout(() => { isLoopingReset = false; }, 30);
      }
      // Wrap right buffer seamlessly
      else if (current > setWidth * 1.6) {
        isLoopingReset = true;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = current - setWidth;
        track.style.scrollBehavior = 'smooth';
        setTimeout(() => { isLoopingReset = false; }, 30);
      }

      // Active dot calculation
      if (dots.length > 0) {
        const step = getItemStep();
        const normalized = ((Math.round(track.scrollLeft / step) % uniqueCount) + uniqueCount) % uniqueCount;
        dots.forEach((dot, idx) => {
          if (idx === normalized) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        });
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        const step = getItemStep();
        track.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        const step = getItemStep();
        track.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    // Dot navigation
    dots.forEach((dot) => {
      dot.addEventListener('click', function () {
        const targetIndex = parseInt(this.getAttribute('data-index'), 10);
        const step = getItemStep();
        const setWidth = getSetWidth();
        const targetScroll = setWidth + (targetIndex * step);
        track.scrollTo({ left: targetScroll, behavior: 'smooth' });
      });
    });

    // Scroll fires far more often than once per frame; without throttling this
    // read/write of scrollLeft thrashes layout while the carousel is moving.
    let scrollTick = false;
    track.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      requestAnimationFrame(function () {
        scrollTick = false;
        handleInfiniteScroll();
      });
    }, { passive: true });

    // Mouse drag support
    let isDown = false;
    let startX;
    let scrollLeftPos;

    track.addEventListener('mousedown', (e) => {
      isDown = true;
      track.style.cursor = 'grabbing';
      startX = e.pageX - track.offsetLeft;
      scrollLeftPos = track.scrollLeft;
    });
    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.style.cursor = '';
    });
    track.addEventListener('mouseup', () => {
      isDown = false;
      track.style.cursor = '';
    });
    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.5;
      track.scrollLeft = scrollLeftPos - walk;
    });

    window.addEventListener('resize', () => {
      cachedStep = null;   // viewport changed, so the measured step is stale
      handleInfiniteScroll();
    });
  }

  function initAll(root) {
    var scope = root || document;
    // On shopify:section:load the event target IS the section element, and
    // querySelectorAll only looks at descendants - so test the root itself too.
    if (scope.matches && scope.matches(SELECTOR)) initSection(scope);
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
